import { AfterViewInit, Component, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of } from 'rxjs';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { MarkdownService } from '../../../services/markdown.service';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';

/** Rendering state of the changelog; `null` while the file is loading. */
type ChangelogState = { html: string } | { error: true } | null;

/**
 * Read-only overlay that renders the repository's CHANGELOG.md.
 * The file is copied into `assets/` at build time (see angular.json), so it
 * always matches the deployed version.
 */
@Component({
  selector: 'app-changelog',
  standalone: true,
  imports: [MessageModule, SkeletonModule],
  templateUrl: './changelog.component.html',
  styleUrl: './changelog.component.scss',
})
export class ChangelogComponent implements AfterViewInit {
  private readonly http = inject(HttpClient);
  private readonly markdownService = inject(MarkdownService);
  private readonly sidebarService = inject(SidebarService);

  private readonly sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-8',
    icon: 'fas fa-clock-rotate-left',
    headerText: 'Changelog',
    subtitle: 'Änderungen je Version',
  };

  readonly changelog = toSignal<ChangelogState>(
    this.http.get('/assets/CHANGELOG.md', { responseType: 'text' }).pipe(
      map(markdown => ({
        html: this.markdownService.convertToHtml(markdown) as string,
      })),
      catchError(() => of({ error: true as const }))
    ),
    { initialValue: null }
  );

  ngAfterViewInit(): void {
    this.sidebarService.setConfiguration(this.sidebarConfig);
  }

  isError(state: ChangelogState): state is { error: true } {
    return !!state && 'error' in state;
  }
}
